<script setup lang="ts">
import type { RejectedFile } from "../composables/useFileUpload";
import { computed, useTemplateRef, watch } from "vue";
import { MIME_TYPE_LABELS, useFileUpload } from "../composables/useFileUpload";
import AwButton from "./AwButton.vue";
import AwIcon from "./AwIcon.vue";

export interface FileUploadProps {
  /**
   * Current files
   */
  files?: File[];
  /**
   * Accepted file types (e.g., ['application/pdf', 'image/jpeg', 'image/*'])
   */
  accept?: string[];
  /**
   * Maximum number of files allowed
   * @default 1
   */
  maxFiles?: number;
  /**
   * Disable file selection
   * @default false
   */
  disabled?: boolean;
  /**
   * UI variant: 'button' (default), 'dropzone', or 'custom' (uses slot)
   */
  variant?: "button" | "dropzone" | "custom";
  /**
   * Custom dropzone title
   */
  dropzoneTitle?: string;
  /**
   * Custom dropzone description
   */
  dropzoneDescription?: string;
}

export interface FileUploadSlots {
  default?: (props: { disabled: boolean; isOverDropZone: boolean; files: File[]; openFileDialog: () => void }) => any;
}

export interface FileUploadEmits {
  (e: "update:files", files: File[]): void;
  (e: "rejected", files: RejectedFile[]): void;
}

export interface FileUploadExposed {
  openFileDialog: () => void;
  clearFiles: () => void;
  addFiles: (files: File[]) => void;
  removeFile: (index: number) => void;
  isOverDropZone: boolean;
  isMaxFilesReached: boolean;
  rejectedFiles: RejectedFile[];
}

const props = withDefaults(defineProps<FileUploadProps>(), {
  files: () => [],
  variant: "dropzone",
  accept: () => [],
  maxFiles: 1,
  disabled: false,
});

const emit = defineEmits<FileUploadEmits>();

const ui = {
  wrapper: "inline-block cursor-pointer",
  dropzone: {
    base: "border-1 rounded-md px-4 py-8 bg-white transition-colors",
    idle: "border-secondary-400 hover:border-secondary-600 active:border-secondary-800",
    active: "border-primary-500 bg-primary-50",
    disabled: "opacity-50 cursor-not-allowed",
  },
};

function formatAcceptType(type: string): string {
  const lower = type.toLowerCase();

  // Check direct mapping
  if (MIME_TYPE_LABELS[lower]) {
    return MIME_TYPE_LABELS[lower];
  }

  // Handle file extensions (.pdf, .jpg, etc.)
  if (lower.startsWith(".")) {
    return lower.slice(1).toUpperCase();
  }

  // Fallback: extract subtype and uppercase
  const subtype = lower.split("/")[1];
  return subtype ? subtype.toUpperCase() : lower.toUpperCase();
}

// Drop zone element ref
const dropZoneRef = useTemplateRef("dropZoneRef");

// Writable computed that bridges props/emits - acts as single source of truth
const files = computed({
  get: () => props.files ?? [],
  set: (value) => emit("update:files", value),
});

// Create composable with the bridged files ref
const { isOverDropZone, rejectedFiles, isMaxFilesReached, openFileDialog, addFiles, removeFile, clearFiles } = useFileUpload({
  files,
  dropZoneRef,
  accept: () => props.accept,
  maxFiles: () => props.maxFiles,
  disabled: () => props.disabled,
});

const effectiveDisabled = computed(() => props.disabled || isMaxFilesReached.value);

// Emit rejected files when they change
watch(rejectedFiles, (rejected) => {
  if (rejected.length > 0) {
    emit("rejected", rejected);
  }
});

const formattedAcceptTypes = computed(() => {
  const acceptTypes = props.accept ?? [];
  if (!acceptTypes.length) {
    return "";
  }

  // Get unique labels (image/* already includes PNG, JPG so avoid duplicates)
  const labels = new Set<string>();
  for (const type of acceptTypes) {
    const formatted = formatAcceptType(type);
    // Split in case of "PNG, JPG" from image/*
    for (const label of formatted.split(", ")) {
      labels.add(label);
    }
  }

  return Array.from(labels).join(", ");
});

const defaultDropzoneTitle = computed(() => {
  const maxFilesCount = props.maxFiles || 1;
  const isMultiple = maxFilesCount > 1;
  const base = isMultiple ? "Ajouter un ou plusieurs fichiers" : "Ajouter un fichier";

  if (isMultiple && maxFilesCount !== Infinity) {
    return `${base} (${maxFilesCount} maximum)`;
  }
  return base;
});

const defaultDropzoneDescription = computed(() => {
  const acceptTypes = props.accept ?? [];
  if (!acceptTypes.length) {
    return "";
  }
  return `Fichiers acceptés: ${formattedAcceptTypes.value}`;
});

const dropzoneClass = computed(() => [
  ui.dropzone.base,
  effectiveDisabled.value ? ui.dropzone.disabled : "",
  isOverDropZone.value ? ui.dropzone.active : ui.dropzone.idle,
]);

function handleClick() {
  if (effectiveDisabled.value) {
    return;
  }
  openFileDialog();
}

defineExpose({
  openFileDialog,
  clearFiles,
  addFiles,
  removeFile,
  isOverDropZone,
  isMaxFilesReached,
  rejectedFiles,
});
</script>

<template>
  <div
    role="button"
    tabindex="0"
    :aria-disabled="effectiveDisabled || undefined"
    :class="ui.wrapper"
    @click.stop="handleClick"
    @keydown.enter.stop="handleClick"
    @keydown.space.prevent.stop="handleClick"
  >
    <div ref="dropZoneRef">
      <!-- Button variant (default) -->
      <AwButton v-if="props.variant === 'button'" :disabled="effectiveDisabled"> Upload </AwButton>

      <!-- Dropzone variant -->
      <div v-else-if="props.variant === 'dropzone'" :class="dropzoneClass">
        <div class="flex flex-col items-center">
          <AwIcon name="i-ph-cloud-arrow-up" class="text-secondary-600 mb-2" size="48" />
          <p class="text-md font-medium mb-1">
            {{ props.dropzoneTitle ?? defaultDropzoneTitle }}
          </p>
          <p v-if="props.dropzoneDescription ?? defaultDropzoneDescription" class="text-md text-secondary-800">
            {{ props.dropzoneDescription ?? defaultDropzoneDescription }}
          </p>
        </div>
      </div>

      <!-- Custom variant (slot) -->
      <slot
        v-else-if="props.variant === 'custom'"
        :disabled="effectiveDisabled"
        :is-over-drop-zone="isOverDropZone"
        :files="files"
        :open-file-dialog="openFileDialog"
      />
    </div>
  </div>
</template>
